import { useContext, useState } from "react";
import { ModalTitle } from "@kaltura/ds-react-components";
import { StyledModal, StyledModalContent } from "./StyledComponents";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { EmbedCodeProps, EmbedSettings, PlayerConfig } from "./embed-settings/types";
import { EmbedSettingsAvailableFeatures } from "./embed-settings/embed-helpers/EmbedHelperV7";
import ShareSection, { LinksType } from "./share-section/ShareSection";
import SettingsContainer from "./embed-settings/SettingsContainer";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";

export interface ShareProps {
    title: string;
    embedSettings?: EmbedSettings[];
    availableFeatures?: EmbedSettingsAvailableFeatures;
    embedCodeParams?: EmbedCodeProps;
    playerConfig?: PlayerConfig;
    hideEmbed?: boolean;
    oEmbed?: boolean;
    allowOembedConfig?: boolean;
    isHeroAction?: boolean;
    entryUrl: string;
    size: "medium" | "large";
    isTimingAllowed: boolean;
    modalID?: string;
    links: LinksType;
}

export interface ShareModalProps extends ShareProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Share Component Modal
 */
export const ShareModal = (props: ShareModalProps) => {
    const {
        title,
        open,
        onClose,
        embedSettings,
        availableFeatures,
        embedCodeParams,
        playerConfig,
        hideEmbed = false,
        oEmbed = false,
        allowOembedConfig = false,
        entryUrl,
        isTimingAllowed = false,
        modalID,
        links = [],
    } = props;

    const hasAllSettings = !!(embedSettings && embedCodeParams && playerConfig);
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;
    const initialShowEmbed = isKaf ? hasAllSettings : false;

    const [showEmbed, setShowEmbed] = useState<boolean>(initialShowEmbed);
    const [showOEmbed, setShowOEmbed] = useState<boolean>(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleClose = (analyticsButtonName?: string) => {
        if (analyticsButtonName) {
            sendButtonAnalytics(`${analyticsButtonName} - Close box`, ButtonClickAnalyticsType.CLOSE);
        }

        setTimeout(
            () => {
                // setTimeout prevents flickering of the initial view before modal close
                setShowEmbed(initialShowEmbed);
                setShowOEmbed(false);
            },
            500
        );

        onClose();
    };

    const v7Players = embedSettings?.filter((player) => player.isPlayerV7);
    const showNoPlayers = v7Players?.length === 0;
    // no modal close button for embed/oembed modal or no players message modal
    let onCloseAction: (() => void) | undefined = handleClose;
    if ((showEmbed || showOEmbed) && !showNoPlayers) {
        onCloseAction = undefined;
    }
    // set modal title text
    let modalTitleText = translate("Share");
    if (showEmbed) {
        modalTitleText = translate("Embed");
    }
    else if (showOEmbed) {
        modalTitleText = translate("oEmbed link");
    }

    const showEmbeds = embedSettings && embedSettings.length ? embedSettings.length > 0 : false;

    return (
        <StyledModal
            id={modalID}
            open={open}
            showEmbed={showEmbed || showOEmbed}
            showNoPlayers={showNoPlayers}
            className="kms-ds-media-page-share-modal"
        >
            <ModalTitle closeButtonAriaLabel={translate("Close")} onClose={onCloseAction}>
                {!showNoPlayers && modalTitleText}
            </ModalTitle>

            {!showEmbed && !showOEmbed && (
                <StyledModalContent>
                    <ShareSection
                        title={title}
                        setShowEmbed={setShowEmbed}
                        setShowOEmbed={setShowOEmbed}
                        showEmbeds={showEmbeds}
                        hideEmbed={hideEmbed}
                        oEmbed={oEmbed}
                        entryUrl={entryUrl}
                        isTimingAllowed={isTimingAllowed}
                        links={links}
                    />
                </StyledModalContent>
            )}

            {(showEmbed || showOEmbed) && embedSettings && embedCodeParams && playerConfig && (
                <SettingsContainer
                    embedSettings={embedSettings}
                    availableFeatures={availableFeatures}
                    embedCodeParams={embedCodeParams}
                    playerConfig={playerConfig}
                    onClose={handleClose}
                    embedMethod={showEmbed ? "embed" : "oembed"}
                    allowOembedConfig={showOEmbed ? allowOembedConfig : undefined}
                    isTimingAllowed={isTimingAllowed}
                />
            )}
        </StyledModal>
    );
};

export default ShareModal;
